/**
 * Get Operating Hours for Date
 * 
 * Determines the operating hours for a specific date, considering:
 * 1. Schedule exceptions (holidays, special events)
 * 2. Membership hours (for members)
 * 3. Public hours (default)
 */

import { format, parseISO } from 'date-fns';
import type { WeeklySchedule, ScheduleExceptions, ScheduleExceptionKind, TimeSegment } from '@lgc/database/types/domains/club';
import type { DateString } from '@butter/library/datetime';
import { isFullDayClosure, isPublicScheduleException, subtractDaySchedules } from '@butter/library/availability';

export type OperatingHoursResult = {
  timeWindows: TimeSegment[];
  reason?: string; // For schedule exceptions
  isException: boolean;
  isMembershipHours: boolean;
  kind?: ScheduleExceptionKind; // Present only when isException
  /** False when the club keeps this exception private: its hours still apply, but no notice is shown. */
  isPublic?: boolean; // Present only when isException
  /**
   * Whether a `kind: 'closed'` exception shuts the WHOLE day.
   *
   * Read from the exception itself rather than from `timeWindows`, because this
   * function resolves hours a second time, client-side, and cannot match the
   * server: it picks membership hours OR public hours where the server unions
   * them, and it knows nothing of plan-custom hours. An empty `timeWindows`
   * here therefore does not mean the member has no slots — the grid beside it
   * may be full. The exception's own segments are the same on both sides.
   */
  isClosedAllDay: boolean;
  /** The exception's declared periods, verbatim and sorted. For `closed` these are the CLOSED periods, not `timeWindows`. */
  exceptionSegments: TimeSegment[];
};

/**
 * Get operating hours for a specific date
 * 
 * **Priority Order:**
 * 1. Schedule exceptions (e.g., Christmas, special events)
 *    - `kind: 'hours'` replaces the day's hours with `exception.schedule`
 *    - `kind: 'closed'` subtracts `exception.schedule` from the day's weekly
 *      hours (membership hours for a member, else public hours)
 * 2. Membership hours (if user is a member)
 * 3. Public hours (default)
 *
 * **Handles:**
 * - Schedule exceptions that override or subtract from normal hours
 * - Different hours for members vs public
 * - Segmented hours (closed midday, open again later)
 * - Closed days (empty array)
 * 
 * @param date - Date to check operating hours
 * @param publicHours - Club's normal public hours
 * @param membershipHours - Club's membership hours (optional)
 * @param scheduleExceptions - Schedule exceptions (optional)
 * @param isMember - Whether user is a member
 * @returns Operating hours with time windows
 * 
 * @example
 * // Normal Monday (7 AM - 11 PM)
 * const publicHours = {
 *   monday: [{ startMinutes: 420, endMinutes: 1380 }]
 * };
 * const result = getOperatingHoursForDate(
 *   '2025-12-15', // Monday
 *   publicHours
 * );
 * // Returns: {
 * //   timeWindows: [{ startMinutes: 420, endMinutes: 1380 }],
 * //   isException: false,
 * //   isMembershipHours: false
 * // }
 * 
 * @example
 * // Christmas exception (12 PM - 8 PM)
 * const exceptions = {
 *   "2025-12-25": {
 *     date: "2025-12-25",
 *     reason: "Christmas",
 *     schedule: [{ startMinutes: 720, endMinutes: 1200 }]
 *   }
 * };
 * const result = getOperatingHoursForDate(
 *   '2025-12-25',
 *   publicHours,
 *   undefined,
 *   exceptions
 * );
 * // Returns: {
 * //   timeWindows: [{ startMinutes: 720, endMinutes: 1200 }],
 * //   reason: "Christmas",
 * //   isException: true,
 * //   isMembershipHours: false,
 * //   kind: "hours"
 * // }
 *
 * @example
 * // Closed exception (club closes 3 PM - 6 PM on a normally 7 AM - 11 PM day)
 * const publicHours = {
 *   monday: [{ startMinutes: 420, endMinutes: 1380 }]
 * };
 * const exceptions = {
 *   "2025-12-15": {
 *     date: "2025-12-15",
 *     kind: "closed",
 *     reason: "Maintenance",
 *     schedule: [{ startMinutes: 900, endMinutes: 1080 }]
 *   }
 * };
 * const result = getOperatingHoursForDate(
 *   '2025-12-15', // Monday
 *   publicHours,
 *   undefined,
 *   exceptions
 * );
 * // Returns: {
 * //   timeWindows: [
 * //     { startMinutes: 420, endMinutes: 900 },
 * //     { startMinutes: 1080, endMinutes: 1380 }
 * //   ],
 * //   reason: "Maintenance",
 * //   isException: true,
 * //   isMembershipHours: false,
 * //   kind: "closed"
 * // }
 *
 * @example
 * // Segmented hours (8 AM - 12 PM, 2 PM - 10 PM)
 * const publicHours = {
 *   monday: [
 *     { startMinutes: 480, endMinutes: 720 },   // 8 AM - 12 PM
 *     { startMinutes: 840, endMinutes: 1320 }   // 2 PM - 10 PM
 *   ]
 * };
 * const result = getOperatingHoursForDate(
 *   '2025-12-15',
 *   publicHours
 * );
 * // Returns: {
 * //   timeWindows: [
 * //     { startMinutes: 480, endMinutes: 720 },
 * //     { startMinutes: 840, endMinutes: 1320 }
 * //   ],
 * //   isException: false,
 * //   isMembershipHours: false
 * // }
 * 
 * @example
 * // Member with 24/7 access
 * const membershipHours = {
 *   monday: [{ startMinutes: 0, endMinutes: 1440 }] // Full day
 * };
 * const result = getOperatingHoursForDate(
 *   '2025-12-15',
 *   publicHours,
 *   membershipHours,
 *   undefined,
 *   true // isMember = true
 * );
 * // Returns: {
 * //   timeWindows: [{ startMinutes: 0, endMinutes: 1440 }],
 * //   isException: false,
 * //   isMembershipHours: true
 * // }
 */
export function getOperatingHoursForDate(
  date: DateString,
  publicHours: WeeklySchedule,
  membershipHours?: WeeklySchedule,
  scheduleExceptions?: ScheduleExceptions,
  isMember: boolean = false
): OperatingHoursResult {
  // 1. Get day of week (e.g., "monday", "tuesday"). Must precede the exception
  // check — a 'closed' exception needs the weekly hours to subtract from.
  // parseISO treats 'YYYY-MM-DD' as local midnight — correct for calendar day lookup
  const dayOfWeek = format(parseISO(date), 'EEEE').toLowerCase() as keyof WeeklySchedule;

  // 2. Check for schedule exceptions first (highest priority)
  // DateString is already 'yyyy-MM-dd' — no formatting needed
  const dateKey = date;
  if (scheduleExceptions?.[dateKey]) {
    const exception = scheduleExceptions[dateKey];
    const weeklyForDay =
      isMember && membershipHours?.[dayOfWeek]
        ? membershipHours[dayOfWeek]
        : publicHours[dayOfWeek] || [];
    return {
      // 'hours' replaces the day; 'closed' subtracts from it.
      timeWindows:
        exception.kind === 'closed'
          ? subtractDaySchedules(weeklyForDay, exception.schedule)
          : exception.schedule,
      reason: exception.reason,
      isException: true,
      // True only on the `closed` branch: there the member's weekly hours are
      // the base the closure is subtracted from. An `hours` exception replaces
      // the day outright, so no weekly schedule contributed to it.
      isMembershipHours: exception.kind === 'closed' && isMember && !!membershipHours?.[dayOfWeek],
      kind: exception.kind,
      isPublic: isPublicScheduleException(exception),
      isClosedAllDay: isFullDayClosure(exception),
      exceptionSegments: [...exception.schedule].sort((a, b) => a.startMinutes - b.startMinutes),
    };
  }

  // 3. Use membership hours if member and available
  if (isMember && membershipHours?.[dayOfWeek]) {
    return {
      timeWindows: membershipHours[dayOfWeek],
      isException: false,
      isMembershipHours: true,
      isClosedAllDay: false,
      exceptionSegments: [],
    };
  }

  // 4. Fall back to public hours
  return {
    timeWindows: publicHours[dayOfWeek] || [],
    isException: false,
    isMembershipHours: false,
    isClosedAllDay: false,
    exceptionSegments: [],
  };
}
